import React, { useEffect, useMemo } from "react"
import { FormattedMessage } from "react-intl"
import { useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { RouteComponentProps } from "react-router"
import SideBar from "../../common/layout/sidebar.component.jsx"
import LoadingSpinner from "../../common/spinner/loading.component.jsx"
import CustomTabs, { TabDetails } from "../../common/tab/CustomTabs"
import { GlobalHelper } from "../../indexImports.js"
import ProductService from "../../state/product/ProductService"
import { PRODUCT_TYPE } from "../../state/product/Types"
import { ProductAction } from "../../state/product/actions"
import { ProductSelector } from "../../state/product/selectors"
import ProductEditForm from "../form/edit.component.jsx"
import { ProductInventorySection } from "../sections"
import SerialNumberListingContainer from "../serial-number/SerialNumberListingContainer"
import AppContext from "@lightspeed-invoicing/core-client/frontend/app/Context"

namespace TAB_KEY {
    export const PRODUCT_FORM = "product-form"
    export const SERIAL_NUMBER_LISTING = "serial-number-listing"
    export const INVENTORY = "inventory"
}
type TAB_KEY = typeof TAB_KEY[keyof typeof TAB_KEY]

type Params = {
    productId: string,
}

const ProductEditPage = (props: RouteComponentProps<Params>) => {
    SideBar.activateMenu("product")
    const dispatch = useDispatch()
    const isNonInventory = useSelector(ProductSelector.isNonInventory)

    const queryParams = new URLSearchParams(props.location.search)
    let tabKey = queryParams.get("tab")
    const isValidTabKey = (tabKey: string | null): tabKey is TAB_KEY => {
        if (!tabKey) {
            return false
        }

        return [
            TAB_KEY.PRODUCT_FORM,
            TAB_KEY.SERIAL_NUMBER_LISTING,
        ].includes(tabKey)
    }
    if (!isValidTabKey(tabKey)) {
        tabKey = TAB_KEY.PRODUCT_FORM
    }

    const { productId } = props.match.params
    const [trackSerialNumber, setTrackSerialNumber] = React.useState<boolean>(false)

    const { data: product, isFetching, isLoading, error } = useQuery(
        ["getProduct", productId], () => {
            return ProductService.getProduct(productId)
                .then(p => {
                    p.setId()
                    dispatch(ProductAction.set(p.getDto()))
                    setTrackSerialNumber(p.trackSerialNumber())
                    return p.getDto()
                })
        },
    )
    useEffect(() => {
        return () => {
            dispatch(ProductAction.clear())
        }
    }, [])

    const key = useMemo(() => {
        return btoa(encodeURIComponent(JSON.stringify(product)))
    }, [product])

    if (error) return <></>
    if (!product || isFetching || isLoading) return <LoadingSpinner />

    const tabsMapping: Map<string, TabDetails> = new Map()
    tabsMapping.set(
        TAB_KEY.PRODUCT_FORM,
        {
            component: <ProductEditForm key={key} product={product} />,
            label: <FormattedMessage
                id="product.components.tab.product_label"
                defaultMessage="Product"
            />,
            disabled: false,
        }
    )

    //hide serial numbers for matrix_items for now
    if ([PRODUCT_TYPE.SINGLE].includes(product.attributes.type) && !isNonInventory) {
        tabsMapping.set(
            TAB_KEY.SERIAL_NUMBER_LISTING,
            {
                component: <SerialNumberListingContainer
                    productId={productId}
                    serialNumberTrackingEnabled={trackSerialNumber}
                    onToggleCallback={() => {setTrackSerialNumber(!trackSerialNumber)}}
                />,
                label: <FormattedMessage
                    id="product.components.tab.serial_numbers_label"
                    defaultMessage="Serial Numbers"
                />,
                disabled: false
            }
        )
    }

    if ([PRODUCT_TYPE.SINGLE, PRODUCT_TYPE.MATRIX_ITEM, PRODUCT_TYPE.MATRIX_PARENT].includes(product.attributes.type) &&
        GlobalHelper.canQMInventoryBeEnabled() && AppContext.isInventoryEnabled() && !isNonInventory) {
        tabsMapping.set(
            TAB_KEY.INVENTORY,
            {
                component: <ProductInventorySection
                    product={product}
                />,
                label: <>
                    <FormattedMessage
                        id="product.components.tab.inventory_label"
                        defaultMessage="Inventory"
                    />
                </>,
                disabled: false,
            }
        )
    }

    return <CustomTabs
        key={key}
        tabsMapping={tabsMapping}
        defaultTabKey={tabKey}
    />

}
export default ProductEditPage


// WEBPACK FOOTER //
// ./src/product/pages/ProductEditPage.tsx